import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// Import sub-components
import GasFundManager from './admin/GasFundManager';
import UnallocatedEthManager from './admin/UnallocatedEthManager';
import TokenManager from './admin/TokenManager';
import RebalanceManager from './admin/RebalanceManager';
import UserAnalytics from './admin/UserAnalytics';
import CoinGeckoMapper from './admin/CoinGeckoMapper';
import ChainlinkMapper from './admin/ChainlinkMapper';
import PriceDashboard from './admin/PriceDashboard';
import NotAuthorized from './admin/NotAuthorized';
import GovernancePanel from './admin/GovernancePanel';

const AdminPanel = ({ 
  web3, 
  isOwner = false,   // Contract owner (highest level access)
  isOperator = false, // Fund operator (admin-level access)
  governanceInfo = {},
  onClaimFees = () => {}
}) => {
  // Core state
  const { provider, signer, contract, governanceContract, yildxTokenContract, account, isLoading: web3Loading } = web3 || {};
  const [ownerAddress, setOwnerAddress] = useState('');
  const [activeTab, setActiveTab] = useState('governance'); // Default to governance tab
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // User token and governance info
  const [yildxBalance, setYildxBalance] = useState('0');
  const [votingPower, setVotingPower] = useState('0');
  const [canCreateProposals, setCanCreateProposals] = useState(false);
  const [unclaimedFees, setUnclaimedFees] = useState('0');
  
  // Add state for governance proposal redirect
  const [proposalRedirect, setProposalRedirect] = useState(null);
  
  console.log('AdminPanel: web3 object:', web3);
  console.log('AdminPanel: Governance info provided:', governanceInfo);
  console.log('AdminPanel: Admin status:', {isOwner, isOperator});

  // Check ownership and load governance info
  useEffect(() => {
    const checkAccess = async () => {
      if (contract && account) {
        try {
          console.log("AdminPanel: Checking access for account:", account);
          
          // Get the contract owner
          const owner = await contract.owner();
          setOwnerAddress(owner);
          console.log("AdminPanel: Contract owner is:", owner);
          
          // Get YILDX token balance and governance info if token contract available
          if (yildxTokenContract) {
            try {
              const balance = await yildxTokenContract.balanceOf(account);
              const formattedBalance = ethers.utils.formatEther(balance);
              setYildxBalance(formattedBalance);
              console.log("AdminPanel: YILDX token balance:", formattedBalance);
              
              // Check if user can create proposals (more than 100 YILDX)
              const MIN_TOKENS_TO_VOTE = ethers.utils.parseEther("100"); // 100 YILDX tokens
              const canCreate = balance.gte(MIN_TOKENS_TO_VOTE);
              console.log("AdminPanel: Can create proposals?", canCreate);
              setCanCreateProposals(canCreate);
              
              // Get voting power if governance contract available
              if (governanceContract) {
                try {
                  const votingPower = await governanceContract.getVotingPower(account);
                  const formattedPower = ethers.utils.formatEther(votingPower);
                  console.log("AdminPanel: Voting power:", formattedPower);
                  setVotingPower(formattedPower);
                } catch (votingError) {
                  console.error("AdminPanel: Error fetching voting power:", votingError);
                }
              }
              
              // Get unclaimed fees
              const fees = await yildxTokenContract.pendingFees(account);
              const formattedFees = ethers.utils.formatEther(fees);
              console.log("AdminPanel: Unclaimed fees:", formattedFees);
              setUnclaimedFees(formattedFees);
            } catch (tokenError) {
              console.error("AdminPanel: Error checking token info:", tokenError);
            }
          }
        } catch (err) {
          console.error("AdminPanel: Error checking access:", err);
          setError("Failed to verify access");
        }
      }
    };
    checkAccess();
  }, [contract, yildxTokenContract, governanceContract, account]);
  
  // Handle claiming fees
  const handleClaimFees = async () => {
    if (!yildxTokenContract) {
      setError("YILDX Token contract not available");
      return;
    }
    
    setLoading(true);
    
    try {
      console.log("AdminPanel: Claiming fees...");
      
      // Use the provided handler if available, otherwise use direct contract call
      if (typeof onClaimFees === 'function') {
        await onClaimFees();
      } else {
        const tx = await yildxTokenContract.claimFees();
        console.log("AdminPanel: Fee claim transaction submitted:", tx.hash);
        
        // Wait for transaction to be mined
        const receipt = await tx.wait();
        console.log("AdminPanel: Fee claim transaction mined:", receipt);
      }
      
      // Refresh governance info
      if (yildxTokenContract) {
        const pendingFeesBN = await yildxTokenContract.pendingFees(account);
        const pendingFees = ethers.utils.formatEther(pendingFeesBN);
        setUnclaimedFees(pendingFees);
      }
      
      setSuccess("Successfully claimed governance fees");
    } catch (err) {
      console.error("AdminPanel: Error claiming fees:", err);
      setError(err.message || "Failed to claim fees");
    } finally {
      setLoading(false);
    }
  };
  
  // Add a section to display user access type
  const renderAccessBadge = () => {
    if (isOwner) {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500 text-white">
          Fund Admin
        </div>
      );
    } else if (isOperator) {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500 text-white">
          Fund Operator
        </div>
      );
    } else if (parseFloat(yildxBalance) > 0) {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-500 text-white">
          YILDX Token Holder
        </div>
      );
    }
    
    return (
      <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-500 text-white">
        User
      </div>
    );
  };
  
  // Governance info section
  // const renderGovernanceInfo = () => {
  //   if (parseFloat(yildxBalance) <= 0) return null;
  //
  //   return (
  //     <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 shadow-sm mb-6">
  //       <h3 className="text-lg font-semibold text-gray-800 mb-3">Your YILDX Token Info</h3>
  //
  //       <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
  //         <div className="bg-white rounded-lg p-3 shadow-sm">
  //           <div className="text-sm text-gray-600">Voting Power</div>
  //           <div className="text-2xl font-bold text-indigo-700">{parseFloat(votingPower).toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
  //           <div className="text-xs text-gray-500">
  //             {canCreateProposals ? 'Can create & vote on proposals' : 'Can vote on proposals'}
  //           </div>
  //         </div>
  //
  //         <div className="bg-white rounded-lg p-3 shadow-sm">
  //           <div className="text-sm text-gray-600">YILDX Balance</div>
  //           <div className="text-2xl font-bold text-indigo-700">{parseFloat(yildxBalance).toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
  //           <div className="text-xs text-gray-500">
  //             {parseFloat(yildxBalance) >= 100 ? 'Can create proposals' : 'Need 100 YILDX to create proposals'}
  //           </div>
  //         </div>
  //
  //         <div className="bg-white rounded-lg p-3 shadow-sm">
  //           <div className="text-sm text-gray-600">Pending Fees</div>
  //           <div className="text-2xl font-bold text-indigo-700">{parseFloat(unclaimedFees).toFixed(6)}</div>
  //           <div className="text-xs text-gray-500">ETH available to claim</div>
  //         </div>
  //       </div>
  //
  //       {parseFloat(unclaimedFees) > 0 && (
  //         <div className="flex justify-end">
  //           <button
  //             onClick={handleClaimFees}
  //             disabled={loading}
  //             className={`px-4 py-2 rounded-lg text-white font-medium
  //               ${loading ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'}
  //               focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition
  //               flex items-center`}
  //           >
  //             {loading ? (
  //               <>
  //                 <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
  //                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
  //                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  //                 </svg>
  //                 Claiming...
  //               </>
  //             ) : (
  //               'Claim Fees'
  //             )}
  //           </button>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };
  
  // Not authorized view
  if (!isOwner && !isOperator && parseFloat(yildxBalance) <= 0) {
    console.log("AdminPanel: User not authorized. Showing NotAuthorized component");
    return <NotAuthorized 
      account={account} 
      ownerAddress={ownerAddress} 
      error={error} 
      contract={contract}
    />;
  }

  // Determine which tabs to show - IMPORTANT CHANGE: both isOwner and isOperator can see all tabs
  const showFullAdminPanel = isOwner || isOperator;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Fund Dashboard</h2>
        {renderAccessBadge()}
      </div>
      
      {/*/!* Governance/Token Info *!/*/}
      {/*{renderGovernanceInfo()}*/}
      
      {/* Tabs Navigation - Match exactly the original AdminHeader component */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
        <div className="text-sm text-gray-600">Connected as {isOwner ? 'admin' : isOperator ? 'operator' : 'user'}: {account}</div>
      </div>

      {/* Tabs - Showing exact same tabs as in the original AdminHeader */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'governance' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('governance')}
        >
          Governance
        </button>
        
        {showFullAdminPanel && (
          <>
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'gasFund' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('gasFund')}
            >
              Gas Fund Management
            </button>
            
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'unallocatedEth' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('unallocatedEth')}
            >
              Unallocated ETH
            </button>
            
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'tokens' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('tokens')}
            >
              Token Management
            </button>
            
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'rebalance' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('rebalance')}
            >
              Rebalancing
            </button>
            
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'users' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'chainlink' ? 'text-orange-600 border-b-2 border-orange-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('chainlink')}
            >
              Chainlink Feeds
            </button>
            
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'coingecko' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('coingecko')}
            >
              CoinGecko Mappings
            </button>
            
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'prices' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('prices')}
            >
              Price Dashboard
            </button>
          </>
        )}
      </div>
      
      {/* Notification area */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError('')}
          >
            <span className="text-red-500">×</span>
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{success}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setSuccess('')}
          >
            <span className="text-green-500">×</span>
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'governance' && (
        <GovernancePanel
          fundContract={contract}
          governanceContract={governanceContract}
          yildxTokenContract={yildxTokenContract}
          account={account}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
          proposalRedirect={proposalRedirect}
          setProposalRedirect={setProposalRedirect}
        />
      )}
      
      {activeTab === 'gasFund' && showFullAdminPanel && (
        <GasFundManager 
          contract={contract}
          governanceContract={governanceContract} 
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
          isOwner={isOwner}
          isOperator={isOperator}
          onRedirectToGovernance={(proposalData) => {
            setActiveTab('governance');
            setProposalRedirect(proposalData);
          }}
        />
      )}

      {activeTab === 'unallocatedEth' && showFullAdminPanel && (
        <UnallocatedEthManager
          contract={contract}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
          isOwner={isOwner}
          isOperator={isOperator}
        />
      )}

      {activeTab === 'tokens' && showFullAdminPanel && (
        <TokenManager 
          contract={contract}
          governanceContract={governanceContract}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
          onRedirectToGovernance={(proposalData) => {
            setActiveTab('governance');
            setProposalRedirect(proposalData);
          }}
          isOwner={isOwner}
          isOperator={isOperator}
        />
      )}
      
      {activeTab === 'rebalance' && showFullAdminPanel && (
        <RebalanceManager 
          contract={contract}
          governanceContract={governanceContract}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
          isOwner={isOwner}
          isOperator={isOperator}
          onRedirectToGovernance={(proposalData) => {
            setActiveTab('governance');
            setProposalRedirect(proposalData);
          }}
        />
      )}

      {activeTab === 'users' && showFullAdminPanel && (
        <UserAnalytics
          contract={contract}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
        />
      )}

      {activeTab === 'prices' && showFullAdminPanel && (
        <PriceDashboard
          contract={contract}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
        />
      )}

      {/* Include both Chainlink and CoinGecko components for both admins and operators */}
      {activeTab === 'chainlink' && showFullAdminPanel && (
        <ChainlinkMapper
          contract={contract}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
        />
      )}

      {activeTab === 'coingecko' && showFullAdminPanel && (
        <CoinGeckoMapper
          contract={contract}
          setError={setError}
          setSuccess={setSuccess}
          setLoading={setLoading}
          loading={loading}
        />
      )}
    </div>
  );
};

export default AdminPanel;